// La case a cocher ET le bandeau du panier d'impression, dans UN SEUL fichier :
// les deux disent la meme chose et changent ensemble.
//
// Regles dures (CLAUDE.md) appliquees ici : zero emoji (des icones SVG, trait
// fin, currentColor, meme taille) · fontFamily: "inherit" sur tout bouton ·
// zero texte explicatif a l'ecran, les precisions vont en info-bulle · zero
// dependance nouvelle.

import { revalider } from "../panier";

// ---------------------------------------------------------------------------
// Styles — repris de BlocImpression.js, qui les tient lui-meme de
// ReglagesImpression.js : une seule facon de dessiner un bouton, un cadre.
// fontFamily: "inherit" est obligatoire, sinon le projet retombe en
// Times New Roman.
// ---------------------------------------------------------------------------
const ENCRE = "#122131";
const ROUGE = "#DC2626";
const BOUTON = {
  padding: "8px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13,
  background: "#fff", color: "#374151", cursor: "pointer", fontFamily: "inherit",
};
const BOUTON_PLEIN = {
  ...BOUTON,
  background: ENCRE, border: `1px solid ${ENCRE}`, color: "#fff", fontWeight: 600,
  display: "flex", alignItems: "center", gap: 8,
  // A 375 px le compte et la phrase doivent pouvoir passer a la ligne : sans
  // ca le bouton deborde sur le cote.
  textAlign: "left", whiteSpace: "normal", maxWidth: "100%",
};
const BARRE = {
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
  padding: 12, marginBottom: 12, background: "#fff",
  border: `1px solid ${ENCRE}`, borderRadius: 12,
};
const CADRE_ROUGE = {
  padding: 12, marginBottom: 12, background: "#FEF2F2", border: "1px solid #FECACA",
  borderRadius: 8, color: ROUGE, fontSize: 13, overflowWrap: "anywhere",
};

// Une icone SVG, trait fin, currentColor, meme taille. Regle n. 2 : zero emoji.
function Icone({ chemin }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={chemin} />
    </svg>
  );
}

const TRAIT_IMPRIMANTE = "M6 9V4h12v5M6 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1M6 14h12v6H6z";
const TRAIT_ALERTE = "M12 9v4m0 3h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z";

function texte(valeur) {
  if (valeur === null || valeur === undefined) return "";
  return String(valeur).trim();
}

// ===========================================================================
// La case a cocher — une par commande, dans la liste active.
// ===========================================================================
//
// Elle n'apparait que pour un administrateur, sur une commande « Validé », non
// archivee et NON VERROUILLEE. Une commande verrouillee n'a ni case ni bouton
// (section 2 de la specification) : c'est la traduction visible du verrou, et
// c'est ce qui empeche de recommander une impression deja partie.
export function CasePanier({ estAdmin, commande, verrouillee, cochee, onBasculer }) {
  if (estAdmin !== true) return null;
  if (!commande || typeof commande !== "object") return null;

  // 🔑 On ne RECOPIE pas la regle : on appelle celle qui retire du panier
  // (src/panier.js). Une case qu'on pourrait cocher pour une commande que le
  // panier rejetterait aussitot serait un piege, et deux copies d'une meme
  // regle finissent toujours par diverger.
  const { ids } = revalider([commande.id], [commande], verrouillee === true ? [commande.id] : []);
  if (ids.length !== 1) return null;

  const nom = texte(commande.ref) || texte(commande.id);
  const libelle = `Mettre ${nom} au panier d'impression`;
  return (
    <input
      type="checkbox"
      checked={cochee === true}
      onChange={() => onBasculer(commande.id)}
      // Sans ca, cocher ouvrirait le detail de la commande : la ligne entiere
      // est cliquable. Meme precaution que le menu « ··· ».
      onClick={e => e.stopPropagation()}
      title={libelle}
      aria-label={libelle}
      style={{ width: 16, height: 16, margin: 0, cursor: "pointer", accentColor: ENCRE, flexShrink: 0 }}
    />
  );
}

// ===========================================================================
// Le bandeau — au-dessus de la liste, des qu'une case est cochee.
// ===========================================================================
export default function BandeauPanier({ estAdmin, ids, commandes, retires, onCommander, onVider, onAcquitter }) {
  if (estAdmin !== true) return null;

  const choisis = Array.isArray(ids) ? ids : [];
  const sortis = Array.isArray(retires) ? retires : [];
  if (choisis.length === 0 && sortis.length === 0) return null;

  const parId = new Map();
  if (Array.isArray(commandes)) {
    for (const c of commandes) {
      if (!c || typeof c !== "object") continue;
      const id = texte(c.id);
      if (id && !parId.has(id)) parId.set(id, c);
    }
  }

  // 📌 On compte TOUTES les cases cochees, y compris celles qu'un filtre ou une
  // autre page cache. Compter seulement ce qui est a l'ecran ferait mentir
  // « 3 commandes · 7 plans » des que Simon tape dans la case de recherche.
  const nbCommandes = choisis.length;
  let nbPlans = 0;
  for (const id of choisis) {
    const c = parId.get(texte(id));
    if (c && Array.isArray(c.plans)) nbPlans += c.plans.length;
  }
  const compte = `${nbCommandes} commande${nbCommandes > 1 ? "s" : ""} · ${nbPlans} plan${nbPlans > 1 ? "s" : ""}`;

  return (
    <div>
      {sortis.length > 0 && (
        <div role="alert" style={CADRE_ROUGE}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <Icone chemin={TRAIT_ALERTE} />
            <span>Retirées du panier</span>
          </div>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {sortis.map((r, i) => (
              <li key={`${texte(r && r.id)}-${i}`}>
                {`${texte(r && r.ref) || texte(r && r.id)} : ${texte(r && r.raison)}`}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 8 }}>
            <button type="button" onClick={onAcquitter} style={BOUTON}>J'ai vu</button>
          </div>
        </div>
      )}

      {nbCommandes > 0 && (
        <div style={BARRE}>
          <button
            type="button"
            onClick={() => onCommander(choisis)}
            title="Un seul envoi chez Print.com, donc une seule livraison"
            style={BOUTON_PLEIN}
          >
            <Icone chemin={TRAIT_IMPRIMANTE} />
            {/* 🔴 PAS DE « chez moi » ICI. C'est la derniere phrase que Simon lit
                avant de cliquer, et elle serait FAUSSE des qu'une commande du
                panier part chez son client. La destination se choisit commande
                par commande dans le recapitulatif : le bandeau ne la devance pas. */}
            <span>{`${compte} — Commander l'impression`}</span>
          </button>
          <button type="button" onClick={onVider} title="Décocher toutes les commandes" style={BOUTON}>
            Vider
          </button>
        </div>
      )}
    </div>
  );
}
